/**
 * 心拍数カード
 *
 * 安静時心拍数、最小、最大を表示
 */

import { Heart, HeartPulse } from 'lucide-react'

import type { GarminHeartRateSummary } from '@/api/types/garmin'
import { cn } from '@/lib/utils'

type Props = {
  heartRate: GarminHeartRateSummary | null
  className?: string
}

const getHeartRateZone = (hr: number | null | undefined): { label: string; color: string } => {
  if (hr === null || hr === undefined) return { label: '--', color: 'text-muted-foreground' }
  if (hr < 60) return { label: '低', color: 'text-blue-500' }
  if (hr < 100) return { label: '正常', color: 'text-green-500' }
  if (hr < 140) return { label: '高め', color: 'text-yellow-500' }
  return { label: '高', color: 'text-red-500' }
}

export const HeartRateCard = ({ heartRate, className }: Props) => {
  const zone = getHeartRateZone(heartRate?.resting)

  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <div className="flex items-center gap-2 mb-3">
        <HeartPulse className="size-5 text-red-500" />
        <span className="text-sm font-medium">心拍数</span>
      </div>

      {/* 安静時心拍数 */}
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-4xl font-bold tabular-nums">{heartRate?.resting ?? '--'}</span>
        <span className="text-sm text-muted-foreground">bpm</span>
        <span className={cn('text-sm font-medium ml-auto', zone.color)}>{zone.label}</span>
      </div>

      {/* 心拍数アニメーション */}
      <div className="flex items-center justify-center mb-4">
        <Heart className={cn('size-12 text-red-500', heartRate?.resting && 'animate-pulse')} fill="currentColor" />
      </div>

      {/* 最小・最大 */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">最小</span>
          <span className="font-medium tabular-nums text-blue-600">{heartRate?.min ?? '--'}</span>
        </div>
        <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
          <span className="text-muted-foreground">最大</span>
          <span className="font-medium tabular-nums text-red-600">{heartRate?.max ?? '--'}</span>
        </div>
      </div>
    </div>
  )
}
